import { FC } from 'react';

import type { TemplateSignatoryTypeEntity } from '../../table/row/template-row.types';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { useLocale } from '../../../../../../hooks/index';
import {
  getTemplateSignatoryBaseDisplayLabel,
  getTemplateSignatoryBaseType,
} from '../../../constants/template-signatory-type.constant';
import { SigningMode, signingModeLabelKey } from '../../../enums/template.const';
import { TemplateByIdResponse, useTemplateById } from '../../../hooks/use-template-by-id.hook';
import { TemplateFormStore } from '../../../store/template-form.store';
import { type UpdateTemplateFormSchema } from '../../../validations/edit-template.validation';
import { EditTemplateForm } from './edit-template-form';

const buildDisplayData = (
  template: TemplateByIdResponse,
  messagesLocale: Record<string, string>,
) => {
  const uniqueSignatoryLabels = Array.from(
    new Set(
      (template.signatoryTypes ?? []).map((s: TemplateSignatoryTypeEntity) =>
        getTemplateSignatoryBaseDisplayLabel(s.stakeholderType, messagesLocale),
      ),
    ),
  );
  const signatoriesLabel = uniqueSignatoryLabels.join(', ') || '-';
  const signingMode = template.isSignOrderMatters ? SigningMode.SET_ORDER : SigningMode.ALL_AT_ONCE;
  const signingModeLabel = messagesLocale[signingModeLabelKey[signingMode]] ?? '-';
  const signatoriesOrderLines = uniqueSignatoryLabels.map(
    (label, i: number) => `${i + 1}. ${label}`,
  );
  const fieldMappings = template.fieldMapping
    ? Object.entries(template.fieldMapping).map(([placeholder, field]) => ({ field, placeholder }))
    : [];
  return {
    document: {
      documentName: template.name,
      category: template.category,
      subCategory: template.subCategory,
      equityPlanLabel: template.equityPlan?.name ?? '-',
      expirationPeriod: template.expirationPeriod,
      signatoriesLabel,
      isSignOrderMatters: template.isSignOrderMatters ?? false,
      signingModeLabel,
      signatoriesOrderLines,
    },
    file: {
      documentLink: template.fileLink,
      documentName: template.documentName ?? '-',
      mappedFields: fieldMappings.length,
    },
    fieldMappings,
  };
};

const buildDefaultValues = (template: TemplateByIdResponse): UpdateTemplateFormSchema => {
  const signatories = (template.signatoryTypes ?? []).reduce<
    UpdateTemplateFormSchema['stepTwo']['signatories']
  >((acc, signatory) => {
    const baseStakeholderType = getTemplateSignatoryBaseType(signatory.stakeholderType);
    if (!acc?.includes(baseStakeholderType)) {
      acc?.push(baseStakeholderType);
    }
    return acc;
  }, []);

  return {
    stepOne: {
      name: template.name,
      category: template.category,
      subCategory: template.subCategory,
      equityPlan: template.equityPlan?.id ?? '',
      expireDocument: !!template.expirationPeriod,
      expirationPeriod: template.expirationPeriod ?? undefined,
    },
    stepTwo: {
      signatories,
      signingMode: template.isSignOrderMatters ? SigningMode.SET_ORDER : SigningMode.ALL_AT_ONCE,
      signingOrderMatters: template.isSignOrderMatters,
    },
    stepThree: {
      file: undefined,
      fieldMapping: template.fieldMapping ?? {},
      fieldMappingTypes: {},
      detectedVariables: Object.keys(template.fieldMapping ?? {}),
      tabs:
        template.tabs?.map((t, index) => ({
          type: t.type,
          roleName: getTemplateSignatoryBaseType(t.stakeholderType),
          value: t.signerStakeholderId ?? undefined,
          anchorString: t.anchorString ?? `[legacy-${index + 1}]`,
          pageNumber: t.pageNumber ?? undefined,
          xPosition: t.xPosition ?? undefined,
          yPosition: t.yPosition ?? undefined,
        })) ?? [],
      fileParsed: true,
      mergeFieldIssues: [],
    },
  };
};

export const EditTemplate: FC = () => {
  const templateId = TemplateFormStore(({ templateId }) => templateId);
  const toggleState = TemplateFormStore(({ toggleState }) => toggleState);
  const { messagesLocale } = useLocale();
  const { template, isPending, isError } = useTemplateById(templateId);

  if (!templateId) {
    return null;
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <span className="text-sm text-fireside-600">
          <AppFormattedMessage id="UNEXPECTED_ERROR_OCCURRED" />
        </span>
        <Button
          className="bg-brand-700 px-6 py-2 text-sm font-medium text-white"
          onClick={() => toggleState(false)}
          styleType="DEFAULT_ROUNDED"
          type="button"
        >
          <AppFormattedMessage id="CLOSE" />
        </Button>
      </div>
    );
  }

  if (isPending || !template) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <span className="text-sm text-gray-500">
          <AppFormattedMessage id="LOADING" />
        </span>
      </div>
    );
  }

  const data = buildDisplayData(template, messagesLocale);
  const defaultValues = buildDefaultValues(template);

  return (
    <EditTemplateForm
      data={data}
      defaultValues={defaultValues}
      fileLink={template.fileLink}
      key={`${template.id}-${template.updatedAt}`}
      templateId={templateId}
    />
  );
};
